import { useState, useEffect, useMemo, useRef, useContext } from 'react'
import {
  useMediaQuery,
  InputBase,
  InputAdornment,
  IconButton,
  Grid,
  Container,
  Backdrop,
  CircularProgress,
  makeStyles,
  FormControl,
  Select,
  NoSsr,
  Box,
  Popover,
  Typography,
} from '@material-ui/core'
import WarningIcon from '@material-ui/icons/Warning'

import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import Layout from '../components/common/Layout'
import theme from '../components/common/theme'
import ButtonSp from '../components/ui/ButtonSp'
import Steps from '../components/tracking/Steps'
import DetailCard from '../components/tracking/DetailCard'
import StatusCard from '../components/tracking/StatusCard'
import LL_Tracking from '../components/externalBanner/LL_Tracking'
import CollapseCard from '../components/tracking/CollapseCard'
import CardContainer from '../components/tracking/CardContainer'
import TrackingTab from '../components/tracking/TrackingTab'
import delay from '../components/utility/delay'
import {
  orderStatusToStepMap,
  interOrderStatusToStepMap,
  groupByOrderStatus,
} from '../components/utility/trackingUtility'
import Axios from 'axios'

import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { noSSR } from 'next/dynamic'

import { event } from '../libs/ga'
import { POSITION } from '../libs/ga/gaAction'

import { Context } from '../context'
import TrackingNew from '../components/pages-new/TrackingNew'

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
  },
  page: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexGrow: 1,
    '& h1': {
      marginTop: 0,
      marginBottom: theme.spacing(5),
      fontWeight: 500,
      fontSize: 52,
      ...theme.headerFont,
      [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(2),
      },
    },
    '& h2': {
      fontWeight: 400,
      fontSize: 40,
      marginTop: 0,
    },
    '& h4': {
      fontWeight: 400,
      fontSize: 20,
    },
  },
  trackingDashboard: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  heroImg: {
    width: '100%',
    textAlign: 'center',
    [theme.breakpoints.up('sm')]: {
      background:
        'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),url("/static/img/tracking/tracking-cover.jpg")',
      color: 'white',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-evenly',
      alignItems: 'center',
      padding: theme.spacing(5),
      flexGrow: 1,
    },
    [theme.breakpoints.down('xs')]: {
      background:
        'linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),url("/static/img/tracking/tracking-cover.jpg")',
      padding: `${theme.spacing(2)}px ${theme.spacing(1)}px`,
      '& h1': {
        fontSize: 32,
        color: 'white',
      },
    },
  },
  trackNoInputWrapper: {
    [theme.breakpoints.up('sm')]: {
      width: '100%',
      maxWidth: 600,
      display: 'flex',
      flexDirection: 'row',
    },
  },
  stepsWrapper: {
    [theme.breakpoints.up('sm')]: {
      width: '100%',
      maxWidth: 1000,
      margin: `${theme.spacing(3)}px 0`,
    },
  },
  whiteFieldSelect: {
    backgroundColor: 'white',
    borderRadius: 5,
    width: '100%',
  },
  fieldGrid: {
    padding: theme.spacing(1),
  },
  notFoundSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: `${theme.spacing(10)}px ${theme.spacing(2)}px`,
  },
  notFoundText: {
    fontWeight: 500,
    fontSize: 40,
    marginBottom: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
    },
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  pleaseTryAgain: {
    fontSize: 32,
    [theme.breakpoints.down('xs')]: {
      fontSize: 16,
    },
  },
  resultContainer: {
    width: 'calc(100% - 200px)',

    '& > div': {
      margin: '30px',
    },

    [theme.breakpoints.down('md')]: {
      width: 'calc(100% - 20px)',
    },
  },
}))

const LIMIT_SEARCH = 20

const TrackingPageOld = () => {
  const Router = useRouter()
  const { mode, tracking_code, typeid } = Router.query
  const { onRouting } = useContext(Context)

  const isMobile = useMediaQuery(theme.breakpoints.down('xs'))
  const [trackingNumber, setTrackingNumber] = useState('') // always equal search field text
  const [trackingErrorNumber, setTrackingErrorNumber] = useState('') // will be the last number that failed to search
  const [trackingMode, setTrackingMode] = useState('domestic')
  const [trackingData, setTrackingData] = useState(undefined)
  const [isNotFound, setNotFoundStatus] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [tab, setTab] = useState('all')
  const [anchorEl, setAnchorEl] = useState(null)
  const searchFieldRef = useRef(null)
  const [open, setOpen] = useState('')
  const classes = useStyles()

  const { t } = useTranslation('tracking')

  const setTrackingModeFromUrl = (mode) => {
    if (mode === 'inter') setTrackingMode('inter')
    else setTrackingMode('domestic')
  }

  const handleSearchInter = async (trackingNumber) => {
    const temp = trackingNumber.replaceAll(' ', '').split(',')
    const trackingIdList = new Set(temp)
    let promises = []
    let data = []
    let courierCode = []

    // เช็ค limit จำนวนที่จะ search ได้ต่อครั้ง
    if (trackingIdList.size > LIMIT_SEARCH) {
      setAnchorEl(searchFieldRef.current)

      return
    }

    const fetchData = async (id) => {
      try {
        const response = await Axios.post('/api/tracking/inter', {
          tracking_code: id,
        })

        const processRes = response.data

        data = [...data, processRes]
        setNotFoundStatus(false)
      } catch (err) {
        setNotFoundStatus(true)
        setTrackingErrorNumber((pre) => (pre ? `${pre}, ${id}` : id))
      }
    }

    if (trackingIdList.size > 0) {
      setTrackingData([])
      setTrackingErrorNumber('')

      for (let v of trackingIdList) {
        promises.push(fetchData(v))
      }

      setLoading(() => true)
      await Promise.all(promises)
      await delay(1500)
      if (data.length === trackingIdList.size) {
        const arr = []

        courierCode = data.map((d) => d.courier_code)

        for (let id of trackingIdList) {
          let find = data.find(
            (d) =>
              d.courier_tracking_code?.toUpperCase() === id.toUpperCase() ||
              d.tracking_code?.toUpperCase() === id.toUpperCase()
          )

          if (!arr.includes(find)) {
            arr.push(find)
          }
        }

        const result = groupByOrderStatus(arr)
        setTrackingData(result)

        if (window.gtag) {
          event({
            action: POSITION.TRACKING.SEARCH_TEXT_FIELD.action,
            event_category: POSITION.TRACKING.SEARCH_TEXT_FIELD.event_category,
            value: { amount: trackingIdList.size, courier_code: courierCode },
          })
        }
      } else {
        setNotFoundStatus(true)
      }
      setLoading(() => false)
    }
  }

  const handleSearchDomestic = async (trackingNumber) => {
    const temp = trackingNumber.replaceAll(' ', '').split(',')
    const trackingIdList = new Set(temp)
    let promises = []
    let data = []
    let courierCode = []

    // เช็ค limit จำนวนที่จะ search ได้ต่อครั้ง
    if (trackingIdList.size > LIMIT_SEARCH) {
      setAnchorEl(searchFieldRef.current)

      return
    }

    const fetchData = async (id) => {
      try {
        const response = await Axios.post('/api/tracking/domestic', {
          tracking_code: id,
        })

        const processRes = response.data

        data = [...data, processRes]
        setNotFoundStatus(false)
      } catch (err) {
        setNotFoundStatus(true)
        setTrackingErrorNumber((pre) => (pre ? `${pre}, ${id}` : id))
      }
    }

    setTrackingData([])
    setTrackingErrorNumber('')

    for (let v of trackingIdList) {
      promises.push(fetchData(v))
    }

    setLoading(() => true)
    await Promise.all(promises)
    await delay(1500)

    if (data.length === trackingIdList.size) {
      const arr = []

      courierCode = data.map((d) => d.courier_code)

      for (let id of trackingIdList) {
        let find = data.find(
          (d) =>
            d.courier_tracking_code?.toUpperCase() === id.toUpperCase() ||
            d.tracking_code?.toUpperCase() === id.toUpperCase()
        )

        if (!arr.includes(find)) {
          arr.push(find)
        }
      }

      const result = groupByOrderStatus(arr)
      setTrackingData(result)

      if (window.gtag) {
        event({
          action: POSITION.TRACKING.SEARCH_TEXT_FIELD.action,
          event_category: POSITION.TRACKING.SEARCH_TEXT_FIELD.event_category,
          value: { amount: trackingIdList.size, courier_code: courierCode },
        })
      }
    } else {
      setNotFoundStatus(true)
    }

    setLoading(() => false)
  }

  const handleSearch = (
    trackingMode = 'domestic',
    trackingNumber,
    isSubmitSearch = false
  ) => {
    if (!trackingNumber) return

    if (isSubmitSearch) {
      if ( ! tracking_code || tracking_code.toUpperCase() !== trackingNumber.toUpperCase() ) {
        onRouting(
          '/tracking',
          `mode=${trackingMode}&tracking_code=${trackingNumber}`,
          {
            isReplace: true,
          }
        )
      }
    }

    if (trackingMode === 'inter') handleSearchInter(trackingNumber)
    else handleSearchDomestic(trackingNumber)

    setTab('all')
  }

  const handleCloseAnchor = () => {
    setAnchorEl(null)
  }

  const mapStatusToStep = (deliveryType, status) => {
    if (deliveryType === 'inter') {
      return interOrderStatusToStepMap[status] ?? -1
    } else if (deliveryType === 'domestic') {
      return orderStatusToStepMap[status] ?? -1
    }
    return -1
  }

  // useEffect(() => {
  //   if (!Router.isReady) return

  //   if (!isNotFound && !trackingData && !isLoading) {
  //     setTrackingNumber(tracking_code)
  //     setTrackingModeFromUrl(mode)

  //     handleSearch(mode, tracking_code)
  //   }
  // }, [Router.isReady])

  useEffect(() => {
    if (!Router.isReady) return

    if (!isLoading) {
      const searchMode = mode || typeid

      setTrackingNumber(tracking_code)
      setTrackingModeFromUrl(searchMode)

      handleSearch(searchMode, tracking_code)
    }
  }, [Router.isReady, mode, tracking_code])

  const renderModeSelect = () => {
    return (
      <div className={classes.whiteFieldSelect}>
        <FormControl variant="outlined" fullWidth>
          <Select
            native
            value={trackingMode}
            onChange={(e) => setTrackingMode(e.target.value)}
            inputProps={{
              name: 'trackingMode',
              id: 'tracking-mode-select',
            }}
            style={{ border: 0, height: 55 }}
          >
            <option value="domestic">{t('domestic')}</option>
            <option value="inter">{t('inter')}</option>
          </Select>
        </FormControl>
      </div>
    )
  }

  const renderSearchField = ({ useMobileStyle = false, searchBtnText }) => {
    const inputBaseJsx = (
      <InputBase
        ref={searchFieldRef}
        placeholder={t('tracking-no')}
        fullWidth
        autoFocus
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon color="disabled" />
          </InputAdornment>
        }
        endAdornment={
          <InputAdornment position="end">
            {trackingNumber ? (
              <IconButton onClick={() => setTrackingNumber('')}>
                <CloseIcon color="disabled" fontSize="small" />
              </IconButton>
            ) : (
              false
            )}
          </InputAdornment>
        }
        value={trackingNumber}
        onChange={(e) => setTrackingNumber(e.target.value)}
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleSearch(trackingMode, trackingNumber, true)
          }
        }}
      />
    )

    if (!useMobileStyle)
      return (
        <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
          <div
            style={{
              width: '100%',
              backgroundColor: 'white',
              padding: 10,
              textAlign: 'left',
              borderTopLeftRadius: 5,
              borderBottomLeftRadius: 5,
            }}
          >
            {inputBaseJsx}
          </div>
          <ButtonSp
            style={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderTopRightRadius: 5,
              borderBottomRightRadius: 5,
              width: 100,
            }}
            onClick={() => handleSearch(trackingMode, trackingNumber, true)}
          >
            {searchBtnText}
          </ButtonSp>
        </div>
      )
    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            width: '100%',
            backgroundColor: 'white',
            padding: 10,
            textAlign: 'left',
            borderRadius: 5,
          }}
        >
          {inputBaseJsx}
        </div>
        <ButtonSp
          fullWidth
          style={{ marginTop: 15, height: 50 }}
          onClick={() => handleSearch(trackingMode, trackingNumber, true)}
        >
          {searchBtnText}
        </ButtonSp>
      </div>
    )
  }

  const isRednerExtraTimeLine = (d) => {
    if (d) {
      const { courier_code } = d
      return ['TP2', 'THP', 'T2P'].includes(courier_code)
    } else {
      return false
    }
  }

  const tmpTrackingState = (d) => {
    if (d) {
      const { state } = d
      return state
    } else {
      return null
    }
  }

  const trackingState = (d) => {
    const _isRednerExtraTimeLine = isRednerExtraTimeLine(d)
    const _tmpTrackingState = tmpTrackingState(d)
    const result =
      _tmpTrackingState && _isRednerExtraTimeLine
        ? [
            {
              datetime: d.datetime_shipping,
              description: t('thaipost-start-description'),
              location: t('thaipost-start-location'),
            },
            ..._tmpTrackingState,
          ]
        : _tmpTrackingState
        ? [..._tmpTrackingState]
        : null

    return result
  }

  const handleOpen = (id, offsetTop) => {
    if (id === open) {
      setOpen((pre) => `close-${pre}`)
    } else {
      setOpen(id)
      window.scrollTo(0, offsetTop)
    }
  }

  return (
    <div className={classes.root}>
      <Layout
        title="เช็คพัสดุ เช็คเลขพัสดุ ติดตามพัสดุ เช็คสถานะพัสดุ ทุกขนส่ง SHIPPOP"
        keywords="เช็คพัสดุ, ติดตามพัสดุ, เช็คเลขพัสดุ, ติดตามเลขพัสดุ, เช็คเลขแทร็ค, เลขพัสดุ"
        description="ติดตามสถานะพัสดุ เช็คพัสดุ เช็คเลขพัสดุ เลข Tracking ทุกขนส่งได้ง่ายๆกับ SHIPPOP เพียงแค่กรอกเลขพัสดุของท่าน"
      >
        <div className={classes.page}>
          <section className={classes.heroImg}>
            <h1>{t('tracking-status')}</h1>
            <Grid container justify="space-evenly" style={{ maxWidth: 900 }}>
              <Grid item xs={12} sm={4} className={classes.fieldGrid}>
                {renderModeSelect()}
              </Grid>
              <Grid item xs={12} sm={8} className={classes.fieldGrid}>
                <div className={classes.trackNoInputWrapper}>
                  {renderSearchField({
                    useMobileStyle: isMobile,
                    searchBtnText: t('search'),
                  })}
                  {/* โชว์ popup limit search */}
                  <Popover
                    open={Boolean(anchorEl)}
                    onClose={handleCloseAnchor}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'center',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'center',
                    }}
                  >
                    <Box
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '10px',
                      }}
                    >
                      <WarningIcon
                        style={{ color: '#EEEE33', marginRight: '5px' }}
                      />
                      <Typography>{`${t('search-limit')} ${LIMIT_SEARCH} ${t(
                        'search-unit'
                      )}`}</Typography>
                    </Box>
                  </Popover>
                </div>
              </Grid>
            </Grid>
          </section>
          <NoSsr>
            <LL_Tracking />
          </NoSsr>
          <Box className={classes.resultContainer}>
            {trackingData && !isNotFound && !isLoading && (
              <>
                <TrackingTab
                  tab={tab}
                  setTab={setTab}
                  data={trackingData}
                  onResetOpen={setOpen}
                />
                <CardContainer>
                  {trackingData[tab] &&
                    trackingData[tab].map((item) => (
                      <CollapseCard
                        key={item.courier_tracking_code}
                        trackindId={item.courier_tracking_code}
                        status={item.order_status}
                        open={open}
                        handleOpen={handleOpen}
                        isOpen={trackingData[tab].length === 1}
                      >
                        <section className={classes.trackingDashboard}>
                          <div className={classes.stepsWrapper}>
                            <Steps
                              activeStep={mapStatusToStep(
                                item['delivery_type'],
                                item['order_status']
                              )}
                            />
                          </div>
                          <Container>
                            <Grid
                              container
                              spacing={3}
                              style={{ marginBottom: 70 }}
                            >
                              <Grid item xs={12} md={4}>
                                <DetailCard data={item} />
                              </Grid>
                              <Grid item xs={12} md={8}>
                                <StatusCard data={trackingState(item)} />
                              </Grid>
                            </Grid>
                          </Container>
                        </section>
                      </CollapseCard>
                    ))}
                  {!trackingData[tab] && (
                    <Box
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="h4">
                        {t('not-found-status')}
                      </Typography>
                    </Box>
                  )}
                </CardContainer>
              </>
            )}
          </Box>
          {isNotFound && trackingErrorNumber && !isLoading && (
            <div className={classes.notFoundSection}>
              <span className={classes.notFoundText}>
                <SearchIcon
                  style={{
                    width: isMobile ? 20 : 40,
                    height: isMobile ? 20 : 40,
                    marginRight: theme.spacing(1),
                  }}
                />{' '}
                {t('order-is-not-found')} {trackingErrorNumber}
              </span>
              <span className={classes.pleaseTryAgain}>
                {t('please-try-again')}
              </span>
            </div>
          )}
          <Backdrop open={isLoading} style={{ zIndex: 99999 }}>
            <CircularProgress />
          </Backdrop>
        </div>
      </Layout>
    </div>
  )
}

export const getServerSideProps = async (ctx) => {
  const { locale, query } = ctx

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'header',
        'footer',
        'pdpa',
        'tracking',
        'services',
      ])),
    },
  }
}

// ?new → new-design variant (new chrome, same content); otherwise the old
// page renders unchanged. Wrapper keeps hook order stable across both paths
// (each branch is its own component, so neither shares the other's hooks).
const TrackingPage = (props) => {
  const { query } = useRouter()
  if ('old' in query) return <TrackingPageOld {...props} />
  return <TrackingNew {...props} />
}

export default TrackingPage
